/**
 * Drops a deterministic authenticated session into the real `Auth0Context`,
 * so every `useAuth0()` call site behaves as if the user is logged in as the
 * seeded preview user.
 *
 * This provider supplies authentication only. AuthProvider loads the base role
 * and effective sample identity from the server, including in preview mode.
 * The backend's PREVIEW_MODE_ROLE selects a separate seed identity per role.
 *
 * All methods are module-level constants — the real SDK memoizes them via
 * `useCallback` inside its provider, and consumers (e.g. `useAuth.tsx`) put
 * them in `useCallback` dependency arrays. Returning new function references
 * on every render would trigger infinite re-renders downstream.
 *
 * `logout()` is the meaningful escape hatch: it sets the per-client skip flag
 * (cookie + localStorage) and reloads, after which `DevAuthProvider` mounts
 * the real Auth0 SDK instead and the user is sent through the normal Auth0
 * login flow.
 *
 * The mocked token is a three-segment unsigned JWT shape the backend's
 * dev-mode decoder at `server/middleware/auth.ts` parses successfully.
 */
import type { ReactNode } from 'react';
import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { Auth0Context } from '@auth0/auth0-react';

import { exitPreviewSession } from './previewMode';

const PAYLOAD = btoa(
  JSON.stringify({
    sub: 'preview-user-id',
    email: 'preview@example.com',
    email_verified: true,
  })
);
const MOCK_TOKEN = `eyJhbGciOiJub25lIn0.${PAYLOAD}.sig`;

const MOCK_USER = {
  sub: 'preview-user-id',
  email: 'preview@example.com',
  email_verified: true,
  name: 'Preview User',
};

const MOCK_CLAIMS = { ...MOCK_USER, __raw: MOCK_TOKEN };

const getIdTokenClaims = async () => MOCK_CLAIMS;
const getAccessTokenSilently = async () => MOCK_TOKEN;
const getAccessTokenWithPopup = async () => MOCK_TOKEN;
const loginWithRedirect = async () => {};
const loginWithPopup = async () => {};
const logout = async () => {
  exitPreviewSession();
};
const handleRedirectCallback = async () => ({ appState: undefined });

// `Auth0ContextInterface` has overloaded signatures (e.g. getAccessTokenSilently)
// that the mock doesn't reproduce. The shape matches at runtime — every property
// the real SDK reads is present — so we narrow through `unknown` rather than
// hand-build matching overload signatures for each mocked function.
const MOCK_CONTEXT_VALUE = {
  isAuthenticated: true,
  isLoading: false,
  error: undefined,
  user: MOCK_USER,
  getIdTokenClaims,
  getAccessTokenSilently,
  getAccessTokenWithPopup,
  loginWithRedirect,
  loginWithPopup,
  logout,
  handleRedirectCallback,
} as unknown as Auth0ContextInterface;

interface MockAuth0ProviderProps {
  children: ReactNode;
}

export const MockAuth0Provider = ({ children }: MockAuth0ProviderProps) => (
  <Auth0Context.Provider value={MOCK_CONTEXT_VALUE}>
    {children}
  </Auth0Context.Provider>
);
